import { Injectable, TemplateRef, Type } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { ModalData } from 'src/app/core/models/modal-data.model';

import { SgrResponse } from 'src/app/core/models/response.model';
import { AppVisualContextService } from './app-visual-context.service';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private actionsSubject: BehaviorSubject<{ action: string; params: { [name: string]: any } }>;
  private mobile: boolean;
  private tablet: boolean;

  constructor(private dialogService: MatDialog, private visualContext: AppVisualContextService) {
    this.actionsSubject = new BehaviorSubject(null);
  }

  getActionsSubject(): BehaviorSubject<{ action: string; params: { [name: string]: any } }> {
    return this.actionsSubject;
  }

  clearSubjects() {
    this.actionsSubject.next(null);
  }

  openModalSmall(modal: TemplateRef<any> | Type<any>, data?: ModalData): MatDialogRef<any> {
    return this.openModal('sm', modal, data);
  }

  openModalMedium(modal: TemplateRef<any> | Type<any>, data?: ModalData): MatDialogRef<any> {
    return this.openModal('md', modal, data);
  }

  openModalLarge(modal: TemplateRef<any> | Type<any>, data?: ModalData): MatDialogRef<any> {
    return this.openModal('lg', modal, data);
  }

  openModalExtraLarge(modal: TemplateRef<any> | Type<any>, data?: ModalData): MatDialogRef<any> {
    return this.openModal('xl', modal, data);
  }

  openModalFullScreen(modal: TemplateRef<any> | Type<any>, data?: ModalData): MatDialogRef<any> {
    return this.openModal('full', modal, data);
  }

  closeModal(result: boolean = false) {
    this.actionsSubject.next({ action: 'close', params: { result: result } });
  }

  private getModalConfig(size: string = 'md'): MatDialogConfig<any> {
    this.mobile = window.innerWidth <= 640;
    this.tablet = window.innerWidth > 640 && window.innerWidth <= 1024;
    let sizes = {
      sm: {
        minWidth: this.mobile ? '80dvw' : this.tablet ? '50dvw' : '30dvw',
        maxWidth: this.mobile ? '95dvw' : this.tablet ? '65dvw' : '40dvw',
        minHeight: '20dvh',
        maxHeight: '80dvh',
      },
      md: {
        minWidth: this.mobile ? '80dvw' : this.tablet ? '60dvw' : '45dvw',
        maxWidth: this.mobile ? '95dvw' : this.tablet ? '75dvw' : '60dvw',
        minHeight: '20dvh',
        maxHeight: '85dvh',
      },
      lg: {
        minWidth: this.mobile ? '80dvw' : this.tablet ? '70dvw' : '60dvw',
        maxWidth: this.mobile ? '95dvw' : this.tablet ? '85dvw' : '80dvw',
        minHeight: '20dvh',
        maxHeight: '90dvh',
      },
      xl: { minWidth: '80dvw', maxWidth: '95dvw', minHeight: '20dvh', maxHeight: '95dvh' },
      full: { minWidth: '100dvw', maxWidth: '100dvw', minHeight: '100dvh', maxHeight: '100dvh' },
    };

    const config = sizes[size] || sizes['md']; // O padrão é 'md' se o tamanho não for encontrado

    // Evita foco automático em ações e retorno ao elemento que abriu o diálogo.
    config.autoFocus = false;
    config.restoreFocus = false;
    config.hasBackdrop = true;
    config.disableClose = false;
    config.panelClass = `modal-size-${size}`;

    return config;
  }

  private openModal(size: string, modal: TemplateRef<any> | Type<any>, modalData?: ModalData): MatDialogRef<any> {
    let modalConfig = this.getModalConfig(size);
    const origin = modal instanceof TemplateRef ? this.visualContext.accentForElement(modal.elementRef.nativeElement.parentElement) : this.visualContext.currentAccent;
    const accent = this.visualContext.accentForInputs(modalData?.accent, origin);
    modalConfig.panelClass = [modalConfig.panelClass as string, this.visualContext.classForAccent(accent)];
    if (modalData) modalConfig.data = modalData; // dados para serem enviados para o modal
    let modalRef = this.dialogService.open(modal, modalConfig);

    return modalRef;
  }
}
